import { Modal, type ModalDialogProps } from '@eika/modal';
import useTranslation, { format } from '@translation-utils/use-translation';
import './about-calculations-modal.scss';
import { InfoMessage } from '@eika/attention-message';
import { LinkButton } from '@eika/button';
import { DetailsList, DetailsListItem } from '@eika/details-list';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { formatNumberWithCurrency } from '@eika-infrastruktur/format';
import { useCalculatedResults } from '@react-query/hooks/use-calculated-results';
import { useState } from 'react';
import { formatPercentage } from '../functions';

export const AboutCalculationsModal = (props: ModalDialogProps) => {
    const { aboutCalculations } = useTranslation();

    return (
        <Modal {...props} className="about-calculations-modal" title={aboutCalculations.modalTitle} size="wide">
            <AboutCalculations />
        </Modal>
    );
};

const AboutCalculations = () => (
    <div className="about-calculations-modal__content">
        <CalculationPoints />
        <Inflation />
        <Verdiutvikling />
        <NoTax />
        <NoGuarantee />
        <WeUseFinansNorge />
    </div>
);

const CalculationPoints = () => {
    const { aboutCalculations } = useTranslation();
    const { data: calculatedResults } = useCalculatedResults();

    if (!calculatedResults) {
        return null;
    }

    return (
        <div className="about-calculations-modal__content__calculation-points">
            <p>{aboutCalculations.interestRateCalculation}</p>
            <ul>
                <li>
                    {format(
                        aboutCalculations.bulletPoint1,
                        formatPercentage(calculatedResults.savingsAccountComparison.interestRate),
                    )}
                </li>
                <li>
                    {format(aboutCalculations.bulletPoint2, formatPercentage(calculatedResults.expected.interestRate))}
                </li>
            </ul>
            <p>{aboutCalculations.basedOnFinansNorge}</p>
        </div>
    );
};

const Inflation = () => {
    const { aboutCalculations } = useTranslation();
    const { data: calculatedResults } = useCalculatedResults();
    const [open, setOpen] = useState<boolean>(false);

    if (!calculatedResults) {
        return null;
    }

    const handleOnClick = () => {
        setOpen(true);
    };

    return (
        <div>
            <h3>{aboutCalculations.inflationHeader}</h3>
            <p>
                {format(aboutCalculations.inflationDescription, formatPercentage(calculatedResults.inflationRate, 0))}
            </p>
            <LinkButton icon={IconTypes.SPOERSMAAL_24} onClick={handleOnClick}>
                {aboutCalculations.whatIsInflation}
            </LinkButton>
            <WhatIsInflationModal open={open} setOpen={setOpen} shouldCloseOnOverlayClick />
        </div>
    );
};

const Verdiutvikling = () => {
    const { aboutCalculations } = useTranslation();
    const { data: calculatedResults } = useCalculatedResults();

    if (!calculatedResults) {
        return null;
    }

    return (
        <div>
            <h3>{aboutCalculations.verdiutviklingHeader}</h3>
            <p>{aboutCalculations.verdiutviklingDescription}</p>
            <DetailsList wrapperClassName="about-calculations-modal__content__verdiutvikling">
                <DetailsListItem
                    label={`${aboutCalculations.verdiutviklingHigh} (${formatPercentage(calculatedResults.high.interestRate)})`}
                    value={formatNumberWithCurrency(calculatedResults.high.totalBalance, 'kr', 0)}
                />
                <DetailsListItem
                    label={`${aboutCalculations.verdiutviklingExpected} (${formatPercentage(calculatedResults.expected.interestRate)})`}
                    value={formatNumberWithCurrency(calculatedResults.expected.totalBalance, 'kr', 0)}
                />
                <DetailsListItem
                    label={`${aboutCalculations.verdiutviklingLow} (${formatPercentage(calculatedResults.low.interestRate)})`}
                    value={formatNumberWithCurrency(calculatedResults.low.totalBalance, 'kr', 0)}
                />
            </DetailsList>
        </div>
    );
};

const NoTax = () => {
    const { aboutCalculations } = useTranslation();

    return (
        <div>
            <h3>{aboutCalculations.taxNotIncludedHeader}</h3>
            <p>{aboutCalculations.taxNotIncludedDescription}</p>
        </div>
    );
};

const NoGuarantee = () => {
    const { aboutCalculations } = useTranslation();

    return (
        <div>
            <h3>{aboutCalculations.noGuaranteeHeader}</h3>
            <p>{aboutCalculations.noGuaranteeDescription}</p>
        </div>
    );
};

const WeUseFinansNorge = () => {
    const { aboutCalculations } = useTranslation();

    return (
        <InfoMessage
            text={aboutCalculations.weUseFinansNorge}
            icon={IconTypes.INFO_24}
            clickableItem={
                <Link
                    href="https://www.finansnorge.no/dokumenter/avtaler/avkastningsprognoser/"
                    icon={IconTypes.EKSTERN_24}
                    iconOnRightSide
                    target="_blank"
                >
                    {aboutCalculations.finansNorgeLink}
                </Link>
            }
        />
    );
};

const WhatIsInflationModal = (props: ModalDialogProps) => {
    const { aboutCalculations } = useTranslation();

    return (
        <Modal {...props} title={aboutCalculations.whatIsInflation}>
            <p>{aboutCalculations.whatIsInflationDescription1}</p>
            <p>{aboutCalculations.whatIsInflationDescription2}</p>
        </Modal>
    );
};
